import { cue } from '../audio/cues.js';
import { followPreference } from '../settings/preferences.js';

import { heartbeatIntervalMs, type LowHealthLevel, lowHealthLevel } from './low-health-level.js';

import './low-health-alert.css';

/**
 * The warning of a player close to falling: a red vignette that pulses on
 * the screen's edges and a heartbeat, under a quarter of the health. It
 * reads the health the server already sends (`PlayerState.hp`), once a frame;
 * a fall or a heal above the line ends it. The combat frame
 * (`combat-frame.ts`) stays as it is underneath: this one is harder-edged
 * and beats, so the two read apart.
 */
export class LowHealthAlert {
  private readonly root: HTMLElement;
  private readonly unfollow: () => void;
  private enabled = true;
  private level: LowHealthLevel = 'none';
  /** When the next heartbeat is due, on the scene's clock; one at a time. */
  private nextBeatAt = 0;

  constructor() {
    this.root = document.createElement('div');
    this.root.className = 'low-health';
    this.root.setAttribute('aria-hidden', 'true');
    document.body.append(this.root);
    this.unfollow = followPreference('lowHealthAlert', (enabled) => {
      this.enabled = enabled;
      this.root.hidden = !enabled;
      if (!enabled) this.setLevel('none');
    });
  }

  /** The local player's health this frame, `now` on the scene's clock. */
  update(hp: number, maxHp: number, now: number): void {
    const level = this.enabled ? lowHealthLevel(hp, maxHp) : 'none';
    if (level !== this.level) {
      // Crossing the line beats at once; a step between low and critical keeps the rhythm.
      if (this.level === 'none') this.nextBeatAt = now;
      this.setLevel(level);
    }
    if (level === 'none' || now < this.nextBeatAt) return;
    cue('sfx.heartbeat');
    this.nextBeatAt = now + heartbeatIntervalMs(level);
  }

  destroy(): void {
    this.unfollow();
    this.root.remove();
  }

  private setLevel(level: LowHealthLevel): void {
    this.level = level;
    this.root.classList.toggle('low-health--low', level === 'low');
    this.root.classList.toggle('low-health--critical', level === 'critical');
  }
}
